<template>
  <div class="py-3 h-100">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <p class="mb-0">Work Order</p>
    </div>

    <!-- One card per work order, mirroring the JKR Work Order step. Cards are
         created automatically when a JKR work order is submitted; the user
         fills in the contractor and the PDF here. -->
    <div class="row g-3 mb-3" v-if="wos.length">
      <div class="col-12 col-md-6 col-xl-4" v-for="(wo, index) in wos" :key="wo._id">
        <div class="card bg-dark border-secondary text-light h-100">
          <div class="card-body d-flex align-items-center py-3">
            <div class="flex-shrink-0 me-3 text-center">
              <button
                class="btn btn-dark border border-secondary px-3 py-2 lh-sm"
                v-if="wo.attachment"
                @click="currentPdf = wo.attachment.url"
              >
                <i class="fa-solid fa-file-pdf fa-2x d-block mb-1" />
                <span class="small">View</span>
              </button>
              <button
                class="btn btn-dark border border-secondary px-3 py-2 lh-sm"
                v-else
                @click="pickFile(wo)"
              >
                <i class="fa-solid fa-plus fa-2x d-block mb-1" />
                <span class="small">{{
                  drafts[wo._id] && drafts[wo._id].fileName
                    ? "Change"
                    : "Add PDF"
                }}</span>
              </button>
              <div
                class="small text-secondary mt-1 text-truncate"
                style="max-width: 90px"
                v-if="!wo.attachment && drafts[wo._id] && drafts[wo._id].fileName"
                :title="drafts[wo._id].fileName"
              >
                {{ drafts[wo._id].fileName }}
              </div>
            </div>
            <div class="text-start overflow-hidden w-100">
              <div class="fw-bold text-nowrap">
                Work Order {{ index + 1 }}
                <span class="badge bg-secondary ms-1" v-if="index > 0">
                  Extension
                </span>
              </div>
              <div class="small mt-1">
                <i class="fa-regular fa-calendar me-1" />
                {{
                  wo.startDate
                    ? `Starts ${Helper.formatDate(wo.startDate)}`
                    : "No start date"
                }}
              </div>
              <div class="small mt-1" v-if="wo.contractor">
                <i class="fa-solid fa-helmet-safety me-1" />
                {{ wo.contractor.name }}
              </div>
              <div class="mt-1" v-else>
                <select
                  class="form-select form-select-sm"
                  :value="(drafts[wo._id] && drafts[wo._id].contractor) || ''"
                  @change="setContractor(wo, $event.target.value)"
                >
                  <option value="" disabled>-- Select contractor</option>
                  <option
                    :value="contractor.id"
                    v-for="contractor in contractors"
                    :key="contractor.id"
                  >
                    {{ contractor.name }}
                  </option>
                </select>
              </div>
              <button
                class="btn btn-sm btn-success mt-2"
                v-if="!isComplete(wo)"
                :disabled="!canSubmit(wo)"
                @click="submitWo(wo)"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="p-4 text-center fst-italic text-secondary" v-if="!wos.length">
      No work order yet — submitted JKR work orders appear here automatically.
    </div>

    <pdf-viewer :url="currentPdf" v-if="currentPdf" @close="currentPdf = null" />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import PdfViewer from "@/components/PdfViewer";

export default {
  props: ["componentId", "nodStatus", "nodId", "caseId"],
  components: { PdfViewer },
  data() {
    return {
      wos: [],
      contractors: [],
      drafts: {},
      currentPdf: null,
    };
  },
  methods: {
    isComplete(wo) {
      return !!(wo.contractor && wo.attachment);
    },
    canSubmit(wo) {
      const d = this.drafts[wo._id] || {};
      const contractor = wo.contractor || d.contractor;
      const file = wo.attachment || d.file;

      return !!(contractor && file);
    },
    setContractor(wo, value) {
      this.drafts = {
        ...this.drafts,
        [wo._id]: { ...(this.drafts[wo._id] || {}), contractor: value },
      };
    },
    pickFile(wo) {
      const input = document.createElement("input");

      input.type = "file";
      input.accept = "application/pdf";

      input.onchange = (e) => {
        const file = e.target.files && e.target.files[0];

        if (!file) return;

        this.drafts = {
          ...this.drafts,
          [wo._id]: {
            ...(this.drafts[wo._id] || {}),
            file,
            fileName: file.name,
          },
        };

        this.Helper.successToast("Work order PDF added");
      };

      input.click();
    },
    submitWo(wo) {
      if (!this.canSubmit(wo)) return;

      Swal.fire({
        icon: "question",
        title: "<h5 class='fw-bold'>Submit work order data?</h5>",
        html: "<p class='mb-0'>This action is not reversible.<br />Data cannot be changed after it is submitted.</p>",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        this.Helper.toggleLoadingSpinner(true);

        const d = this.drafts[wo._id] || {};

        if (!wo.contractor && d.contractor) {
          const [, error] = await this.Helper.handlePromise(
            this.API.put(`nod-wos/${wo._id}`, { contractor: d.contractor })
          );

          if (error) {
            console.error(error);

            this.Helper.toggleLoadingSpinner(false);

            return this.triggerAlert("error", "Error saving contractor");
          }
        }

        if (!wo.attachment && d.file) {
          try {
            await this.uploadFile(wo._id, d.file);
          } catch (uploadError) {
            console.error(uploadError);

            this.Helper.toggleLoadingSpinner(false);

            await this.getWos();

            return this.triggerAlert(
              "error",
              "Contractor saved but the PDF upload failed — please re-upload"
            );
          }
        }

        const componentStatus = this.nodStatus;

        componentStatus.wo = true;

        await this.API.put(`nod-cases/${this.nodId}`, {
          componentStatus: componentStatus,
        });

        await this.getWos();

        // Drop only the card that was just submitted. Clearing the whole map
        // here used to discard every sibling card's contractor choice and
        // picked file — including the File object, forcing a re-pick.
        const remainingDrafts = { ...this.drafts };

        delete remainingDrafts[wo._id];

        this.drafts = remainingDrafts;

        this.Helper.toggleLoadingSpinner(false);

        this.triggerAlert(
          "success",
          "WO uploaded",
          "componentIsCompleted",
          wo._id
        );
      });
    },
    async uploadFile(refId, file) {
      return new Promise(async (resolve, reject) => {
        const fileModels = {
          ref: "nod-wo",
          refId: refId,
          field: "attachment",
          files: "",
        };

        const formData = new FormData();
        Object.keys(fileModels).forEach((key) => {
          if (key != "files") {
            formData.append(key, fileModels[key]);
          } else {
            formData.append("files", file, file.name);
          }
        });

        const [, error] = await this.Helper.handlePromise(
          this.API.postForm("upload", formData)
        );

        if (error) {
          return reject(error);
        } else {
          return resolve("OK");
        }
      });
    },
    triggerAlert(icon, text, callback, callbackData) {
      Swal.fire({
        icon: icon,
        title: `<h5>${text}</h5>`,
      }).then(() => {
        if (callback) {
          this.$emit(callback, callbackData);
        }
      });
    },
    async getContractors() {
      const _contractorsName = ["EDGENTA PROPEL BHD", "SRM SETIA SDN BHD"];

      const query = "name_in=" + _contractorsName.join("&name_in=");

      const [call, error] = await this.Helper.handlePromise(
        this.API.get(`contractors?${query}`)
      );

      if (error) {
        console.error(error);
      } else {
        this.contractors = call.data;
      }
    },
    async getWos() {
      if (!this.nodId) return;

      const [call, error] = await this.Helper.handlePromise(
        this.API.get(`nod-wos?nod_case=${this.nodId}&_sort=datetime:ASC`)
      );

      if (error) {
        this.triggerAlert("error", "Error getting wo data");
      } else {
        this.wos = call.data;
      }
    },
  },
  async mounted() {
    this.Helper.toggleLoadingSpinner(true);

    await this.getContractors();

    await this.getWos();

    this.Helper.toggleLoadingSpinner(false);
  },
};
</script>
